import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X, Wand2, Library } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UomSelect } from "@/components/UomSelect";
import { getErrorMessage } from "@/lib/errors";

function RateLibraryDialog({ open, onOpenChange, rates, reload }) {
  const [form, setForm] = useState({ name: "", kind: "MANPOWER", uom: "", output: "" });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!form.name.trim() || !(Number(form.output) > 0)) return toast.error("Enter a name and an output per day above 0");
    setBusy(true);
    try {
      await api.post("/resource-rates", {
        name: form.name.trim(), is_machinery: form.kind === "MACHINERY",
        uom: form.uom || null, output_per_day: Number(form.output),
      });
      setForm({ name: "", kind: form.kind, uom: form.uom, output: "" });
      await reload();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };
  const remove = async (r) => {
    try { await api.delete(`/resource-rates/${r.id}`); await reload(); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="resource-rates-dialog">
        <DialogHeader>
          <DialogTitle>Resource productivity library</DialogTitle>
          <DialogDescription>
            How much work one worker or one machine does in a day. Use the same name as the Trade you log
            (for machinery, the equipment Type, e.g. "Excavator") so planned and actual line up.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-5 items-end">
          <div className="md:col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mason / Excavator" /></div>
          <div><Label>Type</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="MANPOWER">Manpower</SelectItem><SelectItem value="MACHINERY">Machinery</SelectItem></SelectContent>
            </Select></div>
          <div><Label>Unit of work</Label><UomSelect value={form.uom} onChange={(v) => setForm({ ...form, uom: v })} /></div>
          <div><Label>Output per day</Label><Input type="number" min="0" value={form.output} onChange={(e) => setForm({ ...form, output: e.target.value })} /></div>
        </div>
        <Button size="sm" onClick={add} disabled={busy} className="w-fit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add to library</Button>
        <div className="max-h-72 overflow-auto rounded-lg border border-border">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Per day</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rates.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground">Nothing yet</TableCell></TableRow>}
              {rates.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-foreground">{r.name}</TableCell>
                  <TableCell><Badge variant="secondary">{r.is_machinery ? "Machinery" : "Manpower"}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">{Number(r.output_per_day)} {r.uom || ""}</TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="icon" title="Remove" aria-label="Remove" onClick={() => remove(r)}><Trash2 className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Pick the resources each activity needs; planned counts are then calculated from the daily plan. */
export function ResourcePlanTools({ projectId, activities, canEdit, onChanged }) {
  const [rates, setRates] = useState([]);
  const [links, setLinks] = useState([]);
  const [libOpen, setLibOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [r, l] = await Promise.all([api.get("/resource-rates"), api.get(`/projects/${projectId}/activity-resources`)]);
      setRates(r.data.filter((x) => x.is_active));
      setLinks(l.data);
    } catch { /* library needs plant permission; the panel just stays empty */ }
  }, [projectId]);
  useEffect(() => { reload(); }, [reload]);

  const rateById = useMemo(() => Object.fromEntries(rates.map((r) => [r.id, r])), [rates]);
  const idsFor = (itemId) => links.filter((l) => l.wbs_item_id === itemId).map((l) => l.rate_id);

  const save = async (itemId, rateIds) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/activity-resources`, { wbs_item_id: itemId, rate_ids: rateIds });
      (data.warnings || []).slice(0, 3).forEach((w) => toast.warning(w));
      await reload();
      onChanged?.();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };
  const recalc = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/resource-planning/auto`, {});
      toast.success(`Planned counts updated (${data.rows_written} day rows)`);
      (data.warnings || []).slice(0, 3).forEach((w) => toast.warning(w));
      onChanged?.();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };

  if (!canEdit) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="resource-plan-tools">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-medium text-foreground">Plan resources automatically</div>
          <div className="text-xs text-muted-foreground">Pick what each activity needs. Planned count per day = daily planned quantity ÷ output per day, rounded up.</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setLibOpen(true)}><Library className="h-4 w-4" /> Productivity library</Button>
          <Button size="sm" onClick={recalc} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Recalculate</Button>
        </div>
      </div>
      <div className="max-h-80 overflow-auto rounded-lg border border-border">
        <Table>
          <TableHeader><TableRow><TableHead>Activity</TableHead><TableHead>Scope</TableHead><TableHead>Resources needed</TableHead></TableRow></TableHeader>
          <TableBody>
            {activities.map((a) => {
              const ids = idsFor(a.id);
              const free = rates.filter((r) => !ids.includes(r.id));
              return (
                <TableRow key={a.id}>
                  <TableCell className="text-foreground">{a.code} — {a.name}</TableCell>
                  <TableCell className="font-mono text-xs">{a.quantity != null ? `${Number(a.quantity)} ${a.uom || ""}` : "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      {ids.map((id) => rateById[id] && (
                        <Badge key={id} variant="secondary" className="gap-1">
                          {rateById[id].name} · {Number(rateById[id].output_per_day)}/{rateById[id].uom || "day"}
                          <button type="button" disabled={busy} onClick={() => save(a.id, ids.filter((x) => x !== id))}><X className="h-3 w-3" /></button>
                        </Badge>
                      ))}
                      {free.length > 0 && (
                        <Select value="" onValueChange={(v) => save(a.id, [...ids, v])}>
                          <SelectTrigger className="h-7 w-32 text-xs"><SelectValue placeholder="+ Add" /></SelectTrigger>
                          <SelectContent>{free.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <RateLibraryDialog open={libOpen} onOpenChange={setLibOpen} rates={rates} reload={reload} />
    </div>
  );
}
